export type PortalObjectResult = {
  objectId: string;
  objectType: 'finding' | 'signal';
  title?: string;
};

const isPortalObjectType = (
  contentType: string | undefined,
): contentType is PortalObjectResult['objectType'] =>
  contentType === 'finding' || contentType === 'signal';

const readStringField = (
  data: Record<string, unknown>,
  keys: readonly string[],
): string | null => {
  for (const key of keys) {
    const value = data[key];

    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }

  return null;
};

const extractPortalObjectId = ({
  content,
  objectType,
}: {
  content: unknown;
  objectType: PortalObjectResult['objectType'];
}): string | null => {
  if (typeof content === 'string') {
    const labelledIdPattern = new RegExp(
      [
        `\\b${objectType}\\b\\s*(?:created|updated)?\\s*:?\\s*([a-f0-9]{24})\\b`,
        `\\b(?:created|updated)\\s+(?:a\\s+|the\\s+)?${objectType}\\b\\s*:?\\s*([a-f0-9]{24})\\b`,
        `\\b(?:created|updated)\\s+(?:a\\s+|the\\s+)?${objectType}\\b[^\\n]*\\(\\s*ID:\\s*([a-f0-9]{24})\\s*\\)`,
        `\\b${objectType}(?:Id|\\s+ID)?\\b"?\\s*[:=]\\s*"?([a-f0-9]{24})\\b`,
      ].join('|'),
      'i',
    );
    const match = content.match(labelledIdPattern);

    return match?.slice(1).find(Boolean) ?? null;
  }

  if (!content || typeof content !== 'object') {
    return null;
  }

  const contentRecord = content as Record<string, unknown>;
  const directId =
    objectType === 'finding'
      ? readStringField(contentRecord, [
          'findingId',
          'finding_id',
          'findingObjectId',
          'id',
          '_id',
        ])
      : readStringField(contentRecord, ['signalId', 'signal_id', 'id', '_id']);

  if (directId) return directId;

  const nestedObject = contentRecord[objectType];

  if (nestedObject && typeof nestedObject === 'object') {
    const nestedId = extractPortalObjectId({
      content: nestedObject,
      objectType,
    });

    if (nestedId) return nestedId;
  }

  for (const key of ['message', 'result', 'output']) {
    const value = contentRecord[key];

    if (typeof value === 'string') {
      const nestedId = extractPortalObjectId({ content: value, objectType });

      if (nestedId) return nestedId;
    }
  }

  return null;
};

export const readPortalObjectTitle = (input: unknown): string | undefined => {
  if (!input || typeof input !== 'object') {
    return undefined;
  }

  const inputRecord = input as Record<string, unknown>;
  const title =
    inputRecord.title ?? inputRecord.findingTitle ?? inputRecord.signalTitle;

  return typeof title === 'string' && title.trim() ? title : undefined;
};

export const readPortalObjectResult = ({
  input,
  output,
  contentType,
  isCompleted,
}: {
  input: unknown;
  output: unknown;
  contentType: string | undefined;
  isCompleted: boolean;
}): PortalObjectResult | null => {
  if (!isCompleted || !isPortalObjectType(contentType)) {
    return null;
  }

  const objectId = extractPortalObjectId({
    content: output,
    objectType: contentType,
  });

  if (!objectId) return null;

  return {
    objectId,
    objectType: contentType,
    title: readPortalObjectTitle(input),
  };
};

export const getPortalObjectResultLabel = ({
  label,
  objectType,
}: {
  label: string;
  objectType: PortalObjectResult['objectType'];
}): string => {
  const createdLabelPattern = new RegExp(
    `^(.*\\b(?:created|updated)\\s+(?:a\\s+|the\\s+)?${objectType}s?)\\b\\s*:.*$`,
    'i',
  );
  const match = label.match(createdLabelPattern);

  if (match) return match[1].trim();

  const colonIndex = label.indexOf(':');
  const normalizedLabel = label.toLowerCase();

  if (
    colonIndex > -1 &&
    normalizedLabel.includes(objectType) &&
    (normalizedLabel.includes('created') || normalizedLabel.includes('updated'))
  ) {
    return label.slice(0, colonIndex).trim();
  }

  return label;
};
